import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { invoicesAPI } from '../../services/api';

const formatHours = (h) => `${Number(h || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h`;

/**
 * Zeitnachweis an eine bestehende Rechnung hängen, ersetzen oder entfernen.
 *
 * Vorbelegt mit Kunde/Projekt der Rechnung und dem Monat vor dem
 * Rechnungsdatum. Ist die Rechnung aus Zeiten entstanden, werden standardmäßig
 * genau deren Einträge genommen. Weichen die Stunden von den
 * Stunden-Positionen ab, gibt es nur einen Hinweis – bei Pauschalen ist das
 * gewollt.
 */
const TimesheetDialog = ({ invoice, projects, onClose, onChanged, onPreview }) => {
  const [info, setInfo] = useState(null);
  const [selection, setSelection] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!invoice) return;
    setInfo(null);
    setSummary(null);
    setError('');
    invoicesAPI.getTimesheetInfo(invoice.id)
      .then(({ data }) => {
        setInfo(data);
        setSelection({
          mode: data.linked_entry_count > 0 ? 'linked' : 'range',
          date_from: data.defaults.date_from,
          date_to: data.defaults.date_to,
          project_id: data.defaults.project_id || '',
        });
      })
      .catch(() => setError('Zeitnachweis-Daten konnten nicht geladen werden'));
  }, [invoice]);

  // Live-Zählung, sobald sich die Auswahl ändert (leicht entprellt)
  useEffect(() => {
    if (!invoice || !selection) return undefined;
    const handle = setTimeout(() => {
      invoicesAPI.previewTimesheet(invoice.id, { ...selection, project_id: selection.project_id || null })
        .then(({ data }) => { setSummary(data); setSummaryError(''); })
        .catch((err) => { setSummary(null); setSummaryError(err.response?.data?.error || 'Auswahl ungültig'); });
    }, 250);
    return () => clearTimeout(handle);
  }, [invoice, selection]);

  if (!invoice) return null;

  const customerProjects = projects.filter((p) => p.customer_id === invoice.customer_id);
  const hoursDiffer = summary && summary.invoice_hours != null
    && Math.abs(summary.hours - summary.invoice_hours) > 0.009;

  const update = (field) => (e) => setSelection((prev) => ({ ...prev, [field]: e.target.value }));

  const handleAttach = async () => {
    setBusy(true);
    setError('');
    try {
      const { data } = await invoicesAPI.attachTimesheet(invoice.id, {
        ...selection, project_id: selection.project_id || null,
      });
      onChanged(data);
      onClose();
      onPreview?.(data, 'timesheet');
    } catch (err) {
      setError(err.response?.data?.error || 'Zeitnachweis konnte nicht angehängt werden');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Zeitnachweis von dieser Rechnung entfernen?')) return;
    setBusy(true);
    try {
      const { data } = await invoicesAPI.removeTimesheet(invoice.id);
      onChanged(data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Zeitnachweis konnte nicht entfernt werden');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!invoice} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 600, color: '#1e3a5f' }}>
        Zeitnachweis · {invoice.invoice_number}
        <Typography variant="body2" color="text.secondary">
          {invoice.customer_name || invoice.customer?.company_name}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!info || !selection ? (
          !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            {info.has_timesheet && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Es ist bereits ein Zeitnachweis angehängt. „Ersetzen" erzeugt ihn neu.
              </Alert>
            )}

            {info.linked_entry_count > 0 && (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={selection.mode}
                onChange={(e, mode) => mode && setSelection((prev) => ({ ...prev, mode }))}
                sx={{ mb: 2 }}
              >
                <ToggleButton value="linked">Zeiten dieser Rechnung ({info.linked_entry_count})</ToggleButton>
                <ToggleButton value="range">Zeitraum wählen</ToggleButton>
              </ToggleButtonGroup>
            )}

            {selection.mode === 'range' && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <TextField label="Von" type="date" value={selection.date_from} onChange={update('date_from')}
                           InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 140px' }} />
                <TextField label="Bis" type="date" value={selection.date_to} onChange={update('date_to')}
                           InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 140px' }} />
                <TextField select label="Projekt" value={selection.project_id} onChange={update('project_id')}
                           sx={{ flex: '1 1 100%' }}>
                  <MenuItem value="">Alle Zeiten des Kunden</MenuItem>
                  {customerProjects.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                </TextField>
              </Box>
            )}

            {summaryError && <Alert severity="warning">{summaryError}</Alert>}
            {summary && (
              <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 2, p: 2 }}>
                <Typography fontWeight={600}>
                  {summary.entry_count} {summary.entry_count === 1 ? 'Eintrag' : 'Einträge'} · {formatHours(summary.hours)}
                </Typography>
                {summary.groups.map((g) => (
                  <Box key={g.label} sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">{g.label}</Typography>
                    <Typography variant="body2" color="text.secondary">{formatHours(g.hours)}</Typography>
                  </Box>
                ))}
                {summary.entry_count === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Keine Zeiten in dieser Auswahl.
                  </Typography>
                )}
              </Box>
            )}
            {hoursDiffer && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Rechnung: {formatHours(summary.invoice_hours)} · Zeitnachweis: {formatHours(summary.hours)}.
                Anhängen geht trotzdem.
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {info?.has_timesheet && (
          <Button color="error" onClick={handleRemove} disabled={busy} sx={{ mr: 'auto' }}>
            Entfernen
          </Button>
        )}
        <Button onClick={onClose} disabled={busy}>Abbrechen</Button>
        <Button
          variant="contained"
          onClick={handleAttach}
          disabled={busy || !summary || summary.entry_count === 0}
          sx={{ background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)', fontWeight: 600 }}
        >
          {busy ? <CircularProgress size={20} sx={{ color: 'white' }} /> : info?.has_timesheet ? 'Ersetzen' : 'Anhängen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TimesheetDialog;
